import { useMemo } from 'react'
import { documentationRootGuid } from '@constants'
import { filter } from 'lodash-es'
import {
  AttachFile,
  ContentCopy,
  Description,
  LinkOff,
  PictureAsPdf,
  SwapHoriz,
} from '@mui/icons-material'
import { Alert, Button, Chip, Stack, Typography } from '@mui/material'
import { useProjectFiles } from '@editorHooks'
import useDocumentAttachmentMutations from '../../../../../../hooks/useDocumentAttachmentMutations'
import { AttachmentEditing } from '../../../../../../types'
import CropMarginFields from '../../../../../CropMarginFields'
import PageSelectionField from '../../../../../PageSelectionField'
import UploadAttachmentButton from '../UploadAttachmentButton'

interface Props {
  item: FileItem
  attachmentEditing: AttachmentEditing
  /** Page count of the attached file shown in the viewer, if loaded */
  pageCount?: number
}

const AttachmentSection = ({ item, attachmentEditing, pageCount }: Props) => {
  const { onShowFileChange, onAttachmentChanged } = attachmentEditing
  const { uploadAttachment, removeAttachment, copyAttachment, isMutatingAttachment } =
    useDocumentAttachmentMutations(onAttachmentChanged)

  const { data: documentTreeData } = useProjectFiles(documentationRootGuid)

  const attachment = item.attachment
  const isPdf = attachment?.mimetype === 'application/pdf'

  // Other pages can show the same file, editing it changes all of them
  const usageCount = useMemo(
    () =>
      attachment
        ? filter(documentTreeData.files, file => file.attachment?.guid === attachment.guid).length
        : 0,
    [attachment, documentTreeData.files],
  )

  if (!attachment) {
    return (
      <Stack spacing={1}>
        <Typography variant="subtitle2">Datei</Typography>
        <Typography variant="body2" color="text.secondary">
          Keine Datei angehängt
        </Typography>
        <Stack direction="row">
          <UploadAttachmentButton
            label="Datei anhängen"
            icon={<AttachFile />}
            disabled={isMutatingAttachment}
            onFileSelected={file => uploadAttachment({ item, file })}
          />
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">Datei</Typography>
      <Stack direction="row">
        <Chip
          icon={isPdf ? <PictureAsPdf /> : <Description />}
          label={attachment.name}
          variant="outlined"
          size="small"
        />
      </Stack>

      {/* PDFs can't be edited, only a Word file's content is shared */}
      {!isPdf && usageCount > 1 && (
        <Alert severity="info">
          Diese Datei wird an {usageCount} Stellen verwendet. Änderungen am Inhalt der Datei werden
          überall übernommen.
        </Alert>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {!isPdf && usageCount > 1 && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopy />}
            disabled={isMutatingAttachment}
            onClick={async () => {
              try {
                await copyAttachment(item)
                onShowFileChange(true)
              } catch {
                // Errors are handled by the mutation's onError
              }
            }}
          >
            Kopie anlegen
          </Button>
        )}
        <UploadAttachmentButton
          label="Datei ersetzen"
          icon={<SwapHoriz />}
          disabled={isMutatingAttachment}
          onFileSelected={file => uploadAttachment({ item, file })}
        />
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<LinkOff />}
          disabled={isMutatingAttachment}
          onClick={() => removeAttachment(item)}
        >
          Datei entfernen
        </Button>
      </Stack>

      {isPdf && (
        <>
          <PageSelectionField pageCount={pageCount} />
          <CropMarginFields />
        </>
      )}
    </Stack>
  )
}

export default AttachmentSection
